import React, { useState, Dispatch, SetStateAction, useCallback } from 'react'
import { View, ViewStyle, StyleSheet } from 'react-native'

import { useTranslation } from 'react-i18next'

import Separator from '../../../components/Separator'
import { StyledPressable } from '../../../components/StyledPressable'
import SvgIcon from '../../../components/SvgIcon'
import Typography, {
  TypographyInput,
} from '../../../components/Text/Typography'
import appStyles from '../../../styles/app-styles'
import Colors from '../../../styles/Colors'
import { convertHexToRGBA } from '../../../utils/colors'

type PdfControlsProps = {
  step: 1 | 2
  pageNumber: number
  /**
   * not optional, may be loaded later
   */
  pageCount: number | undefined
  changePdfPage: (pageNumber: number) => void
  setPageNumber: Dispatch<SetStateAction<number>>
  style?: ViewStyle
}
export function PdfControls({
  step,
  style,
  pageCount,
  pageNumber,
  setPageNumber,
  changePdfPage,
}: PdfControlsProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'components.pdfControl',
  })
  const [pageInput, setPageInput] = useState<string>()

  const getOnIncrementPagePress = useCallback(
    (direction: 'forward' | 'backward') => {
      return () =>
        setPageNumber(current => {
          const newNumber =
            direction === 'forward'
              ? Math.min(pageCount ?? Infinity, current + step)
              : Math.max(1, current - step)
          changePdfPage(newNumber)
          return newNumber
        })
    },
    [step, pageCount, setPageNumber, changePdfPage],
  )

  const onInputChange = useCallback(
    (input: string) => {
      const number = parseInt(input, 10)
      if (isNaN(number)) {
        setPageInput(input)
      } else {
        const limited = Math.max(1, Math.min(number, pageCount ?? Infinity))
        changePdfPage(limited)
        setPageInput(String(limited))
      }
    },
    [pageCount, setPageInput, changePdfPage],
  )

  const onInputBlur = useCallback(() => setPageInput(undefined), [])

  return (
    <View style={[styles.container, style]}>
      <Typography>{t('pageLabel')}</Typography>
      <Separator width={10} />
      <TypographyInput
        inputMode="numeric"
        style={[
          styles.input,
          {
            width:
              styles.input.width +
              (pageCount ? Math.floor(Math.log10(pageCount)) * 15 : 0),
          },
        ]}
        value={
          pageInput ??
          (step === 1 || pageNumber === pageCount
            ? `${pageNumber}`
            : `${pageNumber}-${pageNumber + 1}`)
        }
        onBlur={onInputBlur}
        onChangeText={onInputChange}
      />

      <Separator width={12} />
      <Typography>
        {t('numberCountSeparator')}
        {pageCount}
      </Typography>

      <Separator width={17} />
      <StyledPressable
        hoverColor={hoverColor}
        pressedColor={pressedColor}
        style={[styles.incrementButton, styles.incrementButtonBackward]}
        disabled={pageNumber === 1}
        onPress={getOnIncrementPagePress('backward')}>
        <View style={styles.backwardIconHolder}>
          <SvgIcon
            name="chevron"
            color={Colors[pageNumber === 1 ? 'layout.gray' : 'layout.black']}
          />
        </View>
      </StyledPressable>
      <View style={styles.buttonDivider} />

      <StyledPressable
        hoverColor={hoverColor}
        pressedColor={pressedColor}
        style={[styles.incrementButton, styles.incrementButtonForward]}
        disabled={pageNumber + step > pageCount!}
        onPress={getOnIncrementPagePress('forward')}>
        <View style={styles.forwardIconHolder}>
          <SvgIcon
            name="chevron"
            color={
              Colors[
                pageNumber + step > pageCount! ? 'layout.gray' : 'layout.black'
              ]
            }
          />
        </View>
      </StyledPressable>
    </View>
  )
}

const hoverColor = convertHexToRGBA(Colors['layout.gray'], 0.1)
const pressedColor = convertHexToRGBA(Colors['layout.gray'], 0.3)
const borderColor = convertHexToRGBA(Colors['layout.gray'], 0.5)
const styles = StyleSheet.create({
  container: {
    ...appStyles.inlineContainer,
    height: 42,
  },
  input: {
    width: 39,
    borderColor,
    height: '100%',
    borderWidth: 1,
    borderRadius: 10,
    textAlign: 'center',
    backgroundColor: Colors['layout.white'],
  },
  incrementButton: {
    ...appStyles.center,
    width: 38,
    borderColor,
    height: '100%',
    borderWidth: 1,
  },
  incrementButtonBackward: {
    borderRightWidth: 0,
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
  },
  incrementButtonForward: {
    borderLeftWidth: 0,
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
  },
  buttonDivider: {
    width: 1,
    height: '100%',
    backgroundColor: borderColor,
  },
  backwardIconHolder: {
    transform: [{ rotate: '90deg' }],
  },
  forwardIconHolder: {
    transform: [{ rotate: '-90deg' }],
  },
})
